import { runtimeConfig } from "config/runtimeConfig";
import React, { useEffect, useState } from "react";
import { toast } from "Components/DesignSystem/Toast/useToast";
import AuthManager from "../../../Common/AuthManager";
import ServiceWrapper from "../../../Services/ServiceWrapper";
import SidePanel from "Components/DesignSystem/SidePanel/SidePanel";
import IconButton from "Components/DesignSystem/IconButton/IconButton";
import Typography from "Components/DesignSystem/Typography/Typography";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import { Copy, Cross, Refresh } from "Components/DesignSystem/Iconography/Icons";

const MID_WAY_URL = runtimeConfig.CONNECTION_API;

/*
    Display-only typesetting, shared shape with the Review Queue and Rules Queue
    logs (2026-09-05): "<timestamp> UTC: <message>" renders as a metadata caption
    over the message. This BE's colon spacing wanders ("UTC :Error", "UTC : Price")
    — `\s*:\s*` absorbs it — and a `\n\n`-split entry can hold interior newlines
    (the sftp error's "[Errno 2]" tail), so the message capture is `[\s\S]*`, kept
    on the pre-wrap body line. A non-matching entry renders verbatim; content is
    never altered.
*/
const LOG_LINE = /^(.+?\bUTC)\s*:\s*([\s\S]*)$/;

const copyToClipboard = (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(() => toast.success("copied"));
  } else {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand("copy");
      toast.success("copied");
    } catch {
      toast.error("Copy failed");
    }
    document.body.removeChild(textarea);
  }
};

/**
 * The ingestion log drawer — "View Logs" on a row of `/gms/connectors`.
 *
 * On DS `SidePanel` since § 132. The fetch, the `\n\n` split, the newest-first
 * reversal and the "Log fetch failed." copy are all unchanged.
 *
 * What the panel itself gains, and what it had:
 *
 * - **A scrim, focus containment and Escape.** The panel was a `.form-header` div:
 *   `position: fixed; width: 34%; right: 1px` with a single `0 2px 16px` shadow and
 *   nothing behind it. The offset pointed DOWN, which contributes nothing to the only
 *   edge a user can see — its left one — so the depth came entirely from 16px of
 *   undirected blur against an undimmed page. `SidePanel` portals to `<body>`, paints
 *   the scrim that actually separates it from the page, and traps Tab inside it.
 * - **The close control is the panel's**, not an `<img src={closeIcon}>` inside a
 *   Bootstrap button with four utility classes.
 *
 * Three defects went with the migration:
 *
 * - **The UID copy control was `<i className="far fa-clipboard" onClick>`.** An `<i>`
 *   is not focusable and takes no keypress, so copying the job id was mouse-only
 *   (WCAG 2.1.1). It is a `Button` with an accessible name now.
 * - **The refresh button was nested INSIDE the `<span>` holding the word "Logs"** —
 *   an interactive control inside the panel's title text. It is a header action.
 * - **An empty log rendered an empty white panel**, indistinguishable from a failed
 *   fetch — the same defect § 109 found in `RulesLog`. It names the state now.
 */
const ConnectionLogs = ({ logData, setViewLog, logId }) => {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const sortedLogs = logData?.split("\n\n").filter((item) => item.trim() !== "").reverse() ?? [];
    setLog(sortedLogs);
  }, [logData]);

  const fetchLog = async () => {
    try {
      setLoading(true);
      const res = await ServiceWrapper.doGet(`${MID_WAY_URL}connection/log/${logId}`, null, null, AuthManager.getGmsToken());
      if (res?.data?.done) {
        const logsString = res?.data?.data?.logs ?? '';
        const sortedLogs = logsString?.split("\n\n").filter((item) => item.trim() !== "").reverse() ?? [];
        setLog(sortedLogs);
      } else {
        toast.error("Log fetch failed.");
      }
    } catch (error) {
      console.error("fetchLog ~ error:", error)
    } finally {
      setLoading(false);
    }
  };

  const entries = log?.filter((logItem) => logItem && logItem.trim() !== "") ?? [];

  return (
    <SidePanel
      isOpen
      onClose={() => setViewLog(false)}
      title="Logs"
      /* `[]`, not omitted — omitting it renders SidePanel's LEGACY Environment tab set
         (§ 53), which is a back-compat default rather than an absence. */
      tabs={[]}
      /* `md` (416px). Log lines are long and wrap more here than at `lg` — that is the
         accepted trade for a drawer that no longer eats a third of the screen. If the
         wrapping ever becomes the complaint, `lg` is the one-word change. */
      width="md"
      data-testid="connectors-logs"
      /* § 326 — the UID rides in the header band, under the title, so it stays put
         while the entries scroll (it is what a user copies into a support thread;
         40 entries in, it used to be off-screen). Same row, testids and copy
         control as before — only where it renders changed. */
      subheader={
        logId ? (
          <div className="tw-flex tw-items-center tw-gap-1.5" data-testid="connectors-logs-uid">
            <Typography variant="caption-metadata" as="span" className="tw-font-medium tw-text-ink-secondary">
              UID:
            </Typography>
            <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary">
              {logId}
            </Typography>
            <IconButton
              size="sm"
              aria-label="Copy log UID"
              data-testid="connectors-logs-copy-btn"
              onClick={() => copyToClipboard(logId)}
            >
              <Copy size={14} />
            </IconButton>
          </div>
        ) : undefined
      }
      /* § 212 IconButtons (2026-09-05) — the Review Queue log's Refresh+X pair;
         testids and accessible names unchanged. Refresh disables and spins in
         flight, which the tertiary Button never signalled. */
      headerActions={
        <>
          <IconButton
            size="sm"
            aria-label="Refresh logs"
            data-testid="connectors-logs-refresh-btn"
            disabled={loading}
            onClick={() => fetchLog()}
          >
            <Refresh className={loading ? "tw-animate-spin" : undefined} />
          </IconButton>
          <IconButton
            size="sm"
            aria-label="Close logs"
            data-testid="connectors-logs-close-btn"
            onClick={() => setViewLog(false)}
          >
            <Cross size={16} />
          </IconButton>
        </>
      }
    >
      {loading ? (
        <div className="tw-flex tw-flex-col tw-gap-3" data-testid="connectors-logs-loading">
          <Skeleton variant="text" lines={4} />
        </div>
      ) : entries.length === 0 ? (
        <EmptyState
          size="sm"
          title="No logs for this run"
          description="This ingestion run finished without writing any log output."
          data-testid="connectors-logs-empty"
        />
      ) : (
        /* No top border: the header band's rule already closes the list's top edge —
           a second one here drew two rules 16px apart. */
        <div className="tw-divide-y tw-divide-edge">
          {entries.map((logItem, index) => {
            const m = LOG_LINE.exec(logItem);
            return (
              <div className="tw-py-3" key={index} data-testid={`connectors-logs-entry-${index}`}>
                {m ? (
                  <>
                    <Typography
                      variant="caption-metadata"
                      as="div"
                      className="tw-text-ink-tertiary tw-tabular-nums"
                    >
                      {m[1]}
                    </Typography>
                    <Typography
                      variant="body-primary"
                      as="p"
                      className="tw-text-ink tw-break-words tw-whitespace-pre-wrap tw-mt-0.5"
                    >
                      {m[2]}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body-primary" as="p" className="tw-break-words tw-whitespace-pre-wrap">
                    {logItem}
                  </Typography>
                )}
              </div>
            );
          })}
        </div>
      )}
    </SidePanel>
  );
};

export default ConnectionLogs;
